import React from "react";
import styled from "styled-components";

import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  FRAME_OUTER_WIDTH,
  FRAME_OUTER_HEIGHT,
  FRAME_SLICE_TOP,
  FRAME_SLICE_LEFT,
} from "../../constants";
import { marcImageUrl } from "../../paths";
import { marcTransformToCss } from "../../virtualCoords";
import { MARC_PICKER_DATA } from "../../marcPickerData";
import FitContainer from "../FitContainer";
import Modal, { ModalTitle } from "../Modal";

// Marc gallery modal — opens at the start of creative mode and any time
// the user clicks "Change Marc." Each tile is a mini gold-framed portrait
// rendered at the canonical FRAME_OUTER × FRAME_OUTER_HEIGHT virtual
// stage and scaled to fit via FitContainer, mirroring DrawingArea's
// structure so the marc inside each tile sits in the exact pose it
// would in author/play mode (orange canvas-bg interior, marc photo
// positioned by marcTransform from the saved reference).
function MarcPickerModal({ open, onOpenChange, marcIds, selectedId, onPick }) {
  return (
    <Modal open={open} onOpenChange={onOpenChange} width={920}>
      <ModalTitle>
        Who do you want to <Accent>draw today?</Accent>
      </ModalTitle>
      <Grid>
        {marcIds.map((id) => (
          <Tile
            key={id}
            type="button"
            $active={id === selectedId}
            onClick={() => onPick(id)}
            aria-label={`Marc ${id}`}
          >
            <PortraitFrame id={id} />
          </Tile>
        ))}
      </Grid>
    </Modal>
  );
}

// One framed marc. The FitContainer holds the canonical FRAME_OUTER
// virtual stage; inside, FrameInterior is the orange canvas-bg layer at
// canvas-virtual dims (positioned at the slice offsets so it sits
// exactly inside frame.png's transparent center), and FrameOverlay is
// the gold frame PNG painted on top. The marc photo is positioned in
// FrameInterior with marcTransformToCss(mult=1) — same call DrawingArea
// uses, since both render inside a FitContainer-scaled stage.
//
// If a marc isn't in MARC_PICKER_DATA (e.g. someone added a thumbnail
// without a committed reference yet) we render with no transform and
// let object-fit: contain do its best.
function PortraitFrame({ id }) {
  const data = MARC_PICKER_DATA[id];
  const transform = data?.marc.transform;
  return (
    <FitContainer width={FRAME_OUTER_WIDTH} height={FRAME_OUTER_HEIGHT}>
      <FrameInterior>
        <MarcImg
          src={marcImageUrl(id)}
          alt=""
          draggable={false}
          style={{
            transform: transform
              ? marcTransformToCss(transform)
              : "translate(-50%, -50%)",
          }}
        />
      </FrameInterior>
      <FrameOverlay src="/frame.png" alt="" draggable={false} />
    </FitContainer>
  );
}

const Accent = styled.span`
  color: var(--color-orange-700);
`;

// Column count is fluid — auto-fit gives 4 → 3 → 2 → 1 as the modal
// narrows, with the inner min(170px, 100%) preventing a single tile
// from overflowing on phone widths (without it, the floor would force
// a horizontal scrollbar inside the modal at sub-170px widths). The
// gap clamps so vertical breathing room shrinks proportionally on
// narrow screens.
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(170px, 100%), 1fr));
  gap: clamp(0.75rem, 2vw, 1.5rem) clamp(0.75rem, 2vw, 1.25rem);
  margin-top: 1.25rem;
`;

// The clickable wrapper around a portrait. We expose --frame-shadow as
// a CSS var on Tile and read it from FrameOverlay's filter — that way
// hover/active swap the gold frame's drop-shadow without applying it to
// the orange interior (which would shadow as a rectangle past the
// frame edges). Default is a soft museum-hang shadow; hover deepens it,
// active layers in a warm orange-700 glow so the current marc reads
// against the already-warm gold frames.
const Tile = styled.button`
  --frame-shadow: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.22));

  position: relative;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 4px;
  aspect-ratio: ${FRAME_OUTER_WIDTH} / ${FRAME_OUTER_HEIGHT};
  transition: transform 140ms ease-out;

  ${(p) =>
    p.$active &&
    `
      transform: translateY(-2px);
      --frame-shadow:
        drop-shadow(0 0 6px var(--color-orange-700))
        drop-shadow(0 10px 14px rgba(0, 0, 0, 0.3));
    `}

  &:hover {
    transform: translateY(-3px);
    --frame-shadow: drop-shadow(0 10px 14px rgba(0, 0, 0, 0.3));
  }

  &:focus-visible {
    outline: 2px solid var(--color-orange-700);
    outline-offset: 4px;
  }
`;

// Sized in canvas-virtual px and positioned at the gold frame's slice
// offsets — the same coordinate space DrawingArea's <Frame> uses. The
// FitContainer above scales it to display alongside FrameOverlay.
const FrameInterior = styled.div`
  position: absolute;
  top: ${FRAME_SLICE_TOP}px;
  left: ${FRAME_SLICE_LEFT}px;
  width: ${CANVAS_WIDTH}px;
  height: ${CANVAS_HEIGHT}px;
  background: var(--canvas-bg);
  overflow: hidden;
`;

// Same shape as OnionMarc.MarcImg — left:50%/top:50% with the centering
// translate baked into marcTransformToCss. width/height: 100% means the
// layout box equals FrameInterior; object-fit: contain preserves the
// natural photo aspect within it.
const MarcImg = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: center center;
  user-select: none;
  pointer-events: none;
`;

// drop-shadow on an img with a transparent center casts shadow only
// from the gold portion's silhouette — the transparent middle doesn't
// throw a shadow over the marc photo behind it. Filter value comes
// from --frame-shadow on the Tile parent so hover/active can swap it.
// transition on filter is what makes the active glow + hover lift feel
// like the frame is responding rather than snapping.
const FrameOverlay = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: ${FRAME_OUTER_WIDTH}px;
  height: ${FRAME_OUTER_HEIGHT}px;
  pointer-events: none;
  user-select: none;
  filter: var(--frame-shadow);
  transition: filter 140ms ease-out;
`;

export default MarcPickerModal;
